import useSWR from "swr";
import React, {useCallback, useMemo} from "react";
import {getFetcher, putFetcher} from "../api/fetchers";
import {Race} from "../../schemas/races/racesSchema";
import {useNotification} from "../hooks/useNotification";

interface RaceContextProps {
    raceId: string;
    children: React.ReactNode;
}

interface RaceContextValue {
    race?: Race,
    isLoading: boolean,
    error?: any,
    updateRace: (race: Race) => Promise<void>,
    refetchRace: () => void,
}

const RaceContext = React.createContext<RaceContextValue | undefined>(undefined);

export const RaceProvider: React.FC<RaceContextProps> = ({raceId, children}) => {
    const {showNotification} = useNotification();
    const {data, mutate, isLoading, error} = useSWR<Race>(`/races/${raceId}`, getFetcher, {keepPreviousData: true});

    const updateRace = useCallback(async (updatedRace: Race) => {
        const raceToUpdate = {...data, ...updatedRace};
        putFetcher(`/races/${raceId}`, {
            raceSeasonId: raceToUpdate.raceSeason.id,
            clubId: raceToUpdate.club?.id,
            ...raceToUpdate,
        })
            .then(() => {
                showNotification("Data závodu byla úspěšně uložena", "success");
                mutate();
            })
            .catch((err) => {
                console.error(err);
                showNotification("Data závodu se nepodařilo uložit", "error");
            });
    }, [data, mutate, raceId, showNotification]);

    if (error) {
        showNotification("Data závodu se nepodařilo načíst", "error");
    }

    const contextValue = useMemo(() => ({
        race: data,
        isLoading,
        error,
        updateRace,
        refetchRace: mutate,
    }), [data, isLoading, error, updateRace, mutate]);

    return (
        <RaceContext.Provider value={contextValue}>
            {children}
        </RaceContext.Provider>
    );
};

export const useRace = () => {
    const context = React.useContext(RaceContext);
    if (!context) throw new Error("useRace must be used within a RaceProvider");
    return context;
};
